import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ToastVariants } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../context/analytics-context/analytics-context';
import { useAppContext } from '../../../context/app-context/app-context';
import { setIsToasterOpen } from '../../../context/app-context/app-context-actions';
import { useInitializationDataContext } from '../../../context/data-context/data-context';
import { AddressRecipientUpdateForm, AddressTypeCode } from '../../../global-types/addresses';
import { useFeatureFlags } from '../../../hooks';
import useAddressSelection from '../../../hooks/use-address-selection';
import { updateAddress } from '../../../services/address';
import { parseUpdateAddressForm } from '../utils/parse-update-address-form';

export const useUpdateAddress = (setRecipientAddressesDialogOpen: (isOpen: boolean) => void) => {
  const { t } = useTranslation();
  const { appDispatch } = useAppContext();
  const {
    initializedDataState: { data },
  } = useInitializationDataContext();

  const { handleUpdateRecipientContact, handleSetSender } = useAddressSelection();
  const { trackUpdateAddressFromAddressSummary } = useAnalyticsContext();
  const { IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED } = useFeatureFlags();

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

  const accountID = data?.account_id;
  const projectId = data?.project_id;

  const formValues = useForm<AddressRecipientUpdateForm>({
    mode: 'onChange',
    delayError: 250,
  });

  const handleCloseEditAddress: () => void = useCallback(() => {
    setOpenEditDialog(false);
    setRecipientAddressesDialogOpen(true);
  }, [setRecipientAddressesDialogOpen]);

  // handleUpdateReturnRecipientAddress handles Recipient Addresses for 1:M Cards
  const handleUpdateReturnRecipientAddress: (address: AddressRecipientUpdateForm) => Promise<void> = useCallback(
    async (address: AddressRecipientUpdateForm) => {
      if (projectId === undefined) {
        setIsToasterOpen(appDispatch, {
          variant: ToastVariants.Error,
          title: t('recipientAddresses.update.errorTitle'),
          children: t('recipientAddresses.update.errorMessage'),
        });
        return;
      }

      // skipping address validation for 1:M cards
      address.skip_usps_validation = true;
      let statusMessage = '';
      updateAddress(
        projectId,
        address.address_id,
        parseUpdateAddressForm(address),
        IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED,
      )
        .then(async (response) => {
          if (response.data) {
            const data = response.data;

            if (data.address_type_code === AddressTypeCode.RECIPIENT) {
              await handleUpdateRecipientContact(
                {
                  ...address,
                  contact_id: response.data.address_id,
                  account_id: accountID || '',
                  address_hash_id: '',
                  contact_type_code: data.address_type_code,
                  is_verified: data.is_verified,
                  created_at: data.created_at,
                  last_updated_at: data.last_updated_at,
                },
                false,
              );
            } else if (data.address_type_code === AddressTypeCode.SENDER) {
              await handleSetSender({
                sender: {
                  ...address,
                  account_id: accountID || '',
                  address_hash_id: '',
                  contact_id: response.data.address_id,
                  contact_type_code: data.address_type_code,
                  is_verified: data.is_verified,
                  created_at: data.created_at,
                  last_updated_at: data.last_updated_at,
                },
                enableRecipientAddresses: true,
              });
            }
          }
        })
        .catch(() => {
          setIsToasterOpen(appDispatch, {
            variant: ToastVariants.Error,
            title: t('recipientAddresses.update.errorTitle'),
            children: t('recipientAddresses.update.errorMessage'),
          });
          statusMessage = t('recipientAddresses.update.errorMessage');
        })
        .finally(() => {
          trackUpdateAddressFromAddressSummary('edit', statusMessage);
          // TODO: When double re-render issue with dialogue has been fixed, uncomment this.
          // setIsToasterOpen(appDispatch, {
          //   variant: ToastVariants.Success,
          //   title: t('addressUpdateSuccess.title'),
          //   children: t('addressUpdateSuccess.message'),
          // });
          handleCloseEditAddress();
        });
    },
    [
      IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED,
      accountID,
      appDispatch,
      handleCloseEditAddress,
      handleUpdateRecipientContact,
      projectId,
      t,
    ],
  );

  return {
    formValues,
    handleUpdateReturnRecipientAddress,
    handleCloseEditAddress,
    dialogState: {
      openEditDialog,
      setOpenEditDialog,
    },
  };
};
